import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'newsquasar-bookmarks';

/**
 * Custom hook for managing bookmarked articles in localStorage.
 *
 * Provides CRUD operations and toast notifications for user feedback.
 *
 * @returns {Object} Bookmark state and actions.
 * @returns {Array} return.bookmarks - Array of saved article objects.
 * @returns {Function} return.isBookmarked - Check if a URL is bookmarked.
 * @returns {Function} return.addBookmark - Save an article.
 * @returns {Function} return.removeBookmark - Remove an article by URL.
 * @returns {Function} return.toggleBookmark - Toggle bookmark status.
 * @returns {Object|null} return.toast - Current toast notification { message, type }.
 */
const useBookmarks = () => {
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [toast, setToast] = useState(null);

  /** Persist bookmarks to localStorage whenever they change. */
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
    } catch {
      // localStorage quota exceeded or unavailable
    }
  }, [bookmarks]);

  /** Show a temporary toast notification. */
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /** Check if an article URL is currently bookmarked. */
  const isBookmarked = useCallback(
    (url) => bookmarks.some((b) => b.url === url),
    [bookmarks]
  );

  /** Save an article to bookmarks. */
  const addBookmark = useCallback(
    (article) => {
      setBookmarks((prev) => {
        if (prev.some((b) => b.url === article.url)) return prev;
        return [...prev, article];
      });
      showToast('Article saved to bookmarks');
    },
    [showToast]
  );

  /** Remove an article from bookmarks by URL. */
  const removeBookmark = useCallback(
    (url) => {
      setBookmarks((prev) => prev.filter((b) => b.url !== url));
      showToast('Article removed from bookmarks', 'info');
    },
    [showToast]
  );

  /** Toggle bookmark status for an article. */
  const toggleBookmark = useCallback(
    (article) => {
      if (isBookmarked(article.url)) {
        removeBookmark(article.url);
      } else {
        addBookmark(article);
      }
    },
    [isBookmarked, addBookmark, removeBookmark]
  );

  return { bookmarks, isBookmarked, addBookmark, removeBookmark, toggleBookmark, toast };
};

export default useBookmarks;
